import React from "react";
import { TitleForm } from "./WorkflowsHeader.types";
import { convertDateRangeToFilter } from "@tiny/utils/dateUtils";
import { useForm } from "react-hook-form";
import { useWorkflowsBuilderPageView } from "../WorkflowsBuilderPage.context";
import { api } from "../../../app/apiInstance";
import { downloadFileData, ResponseHeaders } from "../../../utils/fileUtils";
import { enqueueSnackbar } from "notistack";
import { DEFAULT_ERROR_MESSAGE } from "../../../utils/errorUtils";

const useWorkflowsHeader = () => {
  const {
    getHeaderControlAction,
    quitWithoutSaving,
    downloadEnabled,
    showQuitWorkflows,
    workflowData,
    setWorkflowData,
    getRestrictionOptions,
    viewMode,
    hasUnpublishedChanges,
    isPublishImpactLoading,
    isSavingWorkflow,
    showPublishConfirmation,
  } = useWorkflowsBuilderPageView();

  React.useEffect(() => {
    setWorkflowTitle(workflowData.name);
  }, [workflowData]);

  const [editTitle, setEditTitle] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const [formTitle, setFormTitle] = React.useState<string>(
    workflowData.name || "My First Workflow"
  );
  const [workflowTitle, setWorkflowTitle] =
    React.useState<string>("My First Workflow");
  const toggleEditTitle = () => setEditTitle((prev) => !prev);
  const form = useForm<TitleForm>();
  const saveTitle = async (values: TitleForm) => {
    if (values.name.trim().toLowerCase() === "welcome series") {
      form.setError("name", {
        type: "manual",
        message: '"Welcome Series" is a reserved name and cannot be used.',
      });
      return;
    }
    try {
      setWorkflowData((prev) => ({
        ...prev,
        name: values?.name,
      }));
    } catch (e) {
      return;
      // TODO: handle error
    }
    setWorkflowTitle(values.name);
    toggleEditTitle();
  };

  const quitWorkflows = React.useCallback(() => {
    quitWithoutSaving?.();
  }, []);

  const [showFeatureNotAvailable, setShowFeatureNotAvailable] =
    React.useState(false);
  const closeFeatureNotAvailableDialog = () =>
    setShowFeatureNotAvailable(false);
  const { token } = useWorkflowsBuilderPageView();

  const headerControlActionBase = React.useMemo(() => {
    return getHeaderControlAction();
  }, [
    workflowData,
    hasUnpublishedChanges,
    isPublishImpactLoading,
    isSavingWorkflow,
  ]);

  const headerControlAction = React.useMemo(() => {
    const activateAction = headerControlActionBase.children?.find(
      (action: any) => action.text === "Activate"
    );

    if (activateAction) {
      const originalOnClick = activateAction.onClick;
      activateAction.onClick = () => {
        if (token?.showWorkflows) {
          originalOnClick();
        } else {
          setShowFeatureNotAvailable(true);
        }
      };
    }
    return headerControlActionBase;
  }, [headerControlActionBase, token]);

  const downloadStatistics = React.useCallback(
    async (
      exportStatistics: () => Promise<{ data: unknown; headers: unknown }>
    ) => {
      setIsDownloading(true);
      enqueueSnackbar("Downloading!", {
        variant: "info",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
        autoHideDuration: 2000,
      });
      try {
        const { data, headers } = await exportStatistics();
        downloadFileData(data, headers as ResponseHeaders);
        setIsDownloading(false);
      } catch (error) {
        setIsDownloading(false);
        enqueueSnackbar(DEFAULT_ERROR_MESSAGE, {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
          autoHideDuration: 10000,
        });
      }
    },
    [setIsDownloading]
  );

  const onClickDownload = React.useCallback(
    () =>
      downloadStatistics(() =>
        api.postWorkflowStatisticsExportByWorkflowId(
          workflowData.id,
          {
            format: "blob",
            headers: { "Access-Control-Expose-Headers": "Content-Disposition" },
          },
          {
            from: new Date(workflowData?.createdAt || "").getTime(),
            to: new Date().getTime(),
          }
        )
      ),
    [downloadStatistics, workflowData.id, workflowData?.createdAt]
  );

  // The SEND_SMS-only slice of this workflow's stats
  // (POST /sms/workflow/{id}/statistics/export, served by tiny-svc-reports).
  const onClickDownloadSms = React.useCallback(
    () =>
      downloadStatistics(() =>
        api.postSmsWorkflowStatisticsExportByWorkflowId(
          workflowData.id,
          {
            format: "blob",
            headers: { "Access-Control-Expose-Headers": "Content-Disposition" },
          },
          {
            from: new Date(workflowData?.createdAt || "").toISOString(),
            to: new Date().toISOString(),
          }
        )
      ),
    [downloadStatistics, workflowData.id, workflowData?.createdAt]
  );

  const restrictions = getRestrictionOptions();

  const isWelcomeSeries = workflowData.name?.toLowerCase() === "welcome series";

  // #1956: editing an ACTIVE workflow no longer republishes it, so the canvas and the graph the runtime walks can
  // legitimately differ. Publish is otherwise only reachable inside the Actions menu, where the customer has no reason
  // to look, so surface the state in the header itself. A DRAFT workflow has no published version to diverge from.
  const showPublishState = workflowData.status === "ACTIVE" && !viewMode;

  const allowReentry = !!workflowData.allowReentry;
  const onToggleReentry = (checked: boolean) => {
    setWorkflowData((prev) => ({
      ...prev,
      allowReentry: checked,
    }));
  };

  const hasSmsStep = workflowData.structure?.nodes?.some(
    (node) => node.data?.label === "SMS"
  );

  return {
    onClickDownload,
    onClickDownloadSms,
    hasSmsStep,
    downloadEnabled,
    isDownloading,
    editTitle,
    toggleEditTitle,
    isWelcomeSeries,
    headerControlAction,
    quitWorkflows,
    form,
    formTitle,
    saveTitle,
    setFormTitle,
    titleMaxLength: 64,
    workflowTitle,
    restrictions,
    viewMode,
    showFeatureNotAvailable,
    closeFeatureNotAvailableDialog,
    allowReentry,
    onToggleReentry,
    showPublishState,
    hasUnpublishedChanges,
    isPublishImpactLoading,
    isSavingWorkflow,
    showPublishConfirmation,
  };
};

export default useWorkflowsHeader;
